import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  LayoutDashboard, Award, Globe, MapPin, Users, Clock, Check,
  AlertTriangle, ArrowUpRight, Briefcase, DollarSign, GraduationCap,
  Store, TrendingUp, Plus, ChevronRight, PlayCircle, Trophy, X,
  BookOpen, Target, Compass, Network,
} from "lucide-react";
import { GovernanceBanner, NetworkTab, PayoutCurrencyCard } from "@/pages/partners/PartnerGovernance";

/**
 * /app/partner — Partner Command Center (Phase B).
 *
 * 5 tabs for active partners:
 *   Overview · CRM · Marketplace · Earnings · Academy
 *
 * Non-active branches (no account, pending application) carry over
 * from Phase A.
 */

const TIER_LABEL = { registered: "Registered", silver: "Silver", gold: "Gold", platinum: "Platinum" };
const TIER_TONE = {
  registered: "border-white/20 bg-white/[0.03] text-white/80",
  silver: "border-slate-300/40 bg-slate-200/[0.04] text-slate-100",
  gold: "border-[#D4B878]/50 bg-[#D4B878]/[0.08] text-[#E8D6A3]",
  platinum: "border-[#CCFF00]/50 bg-[#CCFF00]/[0.06] text-[#CCFF00]",
};
const COMMISSION_STATE_META = {
  proposed: { label: "Proposed", tone: "border-white/20 text-white/60" },
  accrued: { label: "Accrued", tone: "border-[#E8D6A3]/40 text-[#E8D6A3]" },
  eligible: { label: "Eligible", tone: "border-[#00E5FF]/40 text-[#00E5FF]" },
  approved: { label: "Approved", tone: "border-[#D4B878]/50 text-[#D4B878]" },
  scheduled: { label: "Scheduled", tone: "border-[#D4B878]/50 text-[#E8D6A3]" },
  paid: { label: "Paid", tone: "border-[#CCFF00]/50 text-[#CCFF00]" },
  held: { label: "Held", tone: "border-amber-400/40 text-amber-300" },
  disputed: { label: "Disputed", tone: "border-red-400/40 text-red-300" },
  reversed: { label: "Reversed", tone: "border-red-500/40 text-red-300" },
  clawed_back: { label: "Clawed back", tone: "border-red-500/40 text-red-300" },
};
const OPP_STAGE_TONE = {
  qualified: "text-white/70", discovery: "text-[#00E5FF]",
  proposal: "text-[#E8D6A3]", negotiation: "text-[#E8D6A3]",
  contracted: "text-[#D4B878]", fulfilment: "text-[#D4B878]",
  won: "text-[#CCFF00]", lost: "text-red-300", cancelled: "text-red-300",
};

const TAB_IDS = ["overview", "crm", "marketplace", "earnings", "network", "academy"];
const TAB_ICONS = {
  overview: LayoutDashboard, crm: Target, marketplace: Compass,
  earnings: DollarSign, network: Network, academy: GraduationCap,
};

export default function PartnerCommandCenter() {
  const { t } = useTranslation();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");

  const TABS = TAB_IDS.map((id) => ({
    id,
    label: t(`partners.${id}`, id.charAt(0).toUpperCase() + id.slice(1)),
    icon: TAB_ICONS[id],
  }));

  const load = () => api.get("/partners/me").then((r) => setMe(r.data));
  useEffect(() => { load(); }, []);

  if (!me) return <div className="max-w-5xl mx-auto px-6 py-10 text-white/40">Loading…</div>;

  const { partner, latest_application } = me;

  if (!partner && !latest_application) {
    return (
      <div data-testid="partner-no-account" className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-3xl sm:text-5xl font-semibold text-white tracking-tight">You're not a partner yet.</h1>
        <p className="mt-4 text-white/60 max-w-2xl">Apply to the Mirrorloops Global Partner Network to unlock the Command Center.</p>
        <Link to="/partners/apply" data-testid="partner-cta-apply"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#CCFF00] text-black font-semibold px-5 py-2.5 text-sm">
          Start application <ArrowUpRight size={13} />
        </Link>
      </div>
    );
  }

  if (!partner && latest_application) {
    return (
      <div data-testid="partner-pending" className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-semibold text-white tracking-tight">Your application is in review.</h1>
        <div className="mt-6 border border-[#E8D6A3]/40 bg-[#E8D6A3]/[0.05] text-[#E8D6A3] rounded-lg p-5">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} />
            <div className="font-semibold text-white uppercase tracking-[0.14em] text-xs">
              {latest_application.status.replace("_", " ")}
            </div>
          </div>
          <div className="text-sm text-white/70">
            Reviewing your {(latest_application.categories || []).join(", ")} application. Decision within 5 business days.
          </div>
          {latest_application.reviewer_note && (
            <div className="mt-3 p-3 border border-white/10 rounded bg-black/20 text-xs text-white/80">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1 font-mono">Reviewer note</div>
              {latest_application.reviewer_note}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="partner-dashboard" className="max-w-6xl mx-auto px-6 py-10">
      <PartnerHeader partner={partner} />
      <GovernanceBanner />
      <nav className="flex gap-1 mt-8 border-b border-white/10 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`partner-tab-${t.id}`}
            className={`text-xs uppercase tracking-[0.18em] font-mono px-4 py-3 border-b-2 whitespace-nowrap transition ${
              tab === t.id
                ? "border-[#D4B878] text-white"
                : "border-transparent text-white/40 hover:text-white/70"
            }`}
          >
            <t.icon size={11} className="inline mr-1.5" />
            {t.label}
          </button>
        ))}
      </nav>
      <div className="mt-6">
        {tab === "overview" && <OverviewTab partner={partner} />}
        {tab === "crm" && <CRMTab partner={partner} />}
        {tab === "marketplace" && <MarketplaceTab partner={partner} />}
        {tab === "earnings" && <EarningsTab partner={partner} />}
        {tab === "network" && <NetworkTab />}
        {tab === "academy" && <AcademyTab />}
      </div>
    </div>
  );
}

function PartnerHeader({ partner }) {
  return (
    <div className="border border-[#D4B878]/25 rounded-lg p-6 bg-gradient-to-br from-[#D4B878]/[0.05] to-transparent">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4B878] mb-2 flex items-center gap-2">
            <LayoutDashboard size={12} /> Partner Command Center
          </div>
          <h1 className="text-3xl font-semibold text-white tracking-tight">
            Welcome back, {partner.name?.split(" ")[0]}.
          </h1>
          <div className="flex items-center gap-3 mt-3 flex-wrap text-xs">
            <span data-testid="partner-tier" className={`font-mono uppercase tracking-[0.14em] px-2 py-0.5 rounded border ${TIER_TONE[partner.tier] || TIER_TONE.registered}`}>
              <Award size={10} className="inline mr-1" /> {TIER_LABEL[partner.tier] || partner.tier}
            </span>
            <span className="text-white/50 font-mono flex items-center gap-1">
              <MapPin size={10} /> {partner.city ? `${partner.city}, ` : ""}{partner.country}
            </span>
            {partner.territory && (
              <span className="text-white/50 font-mono flex items-center gap-1">
                <Globe size={10} /> {partner.territory}
              </span>
            )}
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {(partner.categories || []).map((c) => (
              <span key={c} className="text-[10px] font-mono uppercase tracking-[0.14em] bg-white/[0.04] border border-white/10 rounded-full px-2 py-0.5 text-white/70">
                {c}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------- OVERVIEW ---------------- */
function OverviewTab({ partner }) {
  const [earnings, setEarnings] = useState(null);
  const [opps, setOpps] = useState([]);
  const [academy, setAcademy] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get("/partners/me/earnings").then((r) => setEarnings(r.data)),
      api.get("/partners/me/opportunities?limit=5").then((r) => setOpps(r.data.items)),
      api.get("/partners/me/academy").then((r) => setAcademy(r.data)).catch(() => {}),
    ]);
  }, []);
  return (
    <div className="space-y-6" data-testid="partner-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Pending", value: earnings?.pending_amount, tone: "text-[#E8D6A3]" },
          { label: "Approved", value: earnings?.approved_amount, tone: "text-[#D4B878]" },
          { label: "Paid", value: earnings?.paid_amount, tone: "text-[#CCFF00]" },
          { label: "Active opportunities", value: opps.filter((o) => !["won","lost","cancelled"].includes(o.stage)).length, tone: "text-white", isCount: true },
        ].map((s) => (
          <div key={s.label} data-testid={`overview-stat-${s.label.toLowerCase().replace(/\s/g,"-")}`}
               className="border border-white/10 rounded p-4 bg-white/[0.02]">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">{s.label}</div>
            <div className={`text-2xl font-semibold mt-1 font-mono ${s.tone}`}>
              {s.isCount ? (s.value || 0) : `${earnings?.currency || "INR"} ${(s.value || 0).toLocaleString()}`}
            </div>
          </div>
        ))}
      </div>

      <div className="border border-white/10 rounded-lg p-5 bg-white/[0.02]">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono mb-3">Recent opportunities</div>
        {opps.length === 0 ? (
          <div className="text-sm text-white/40 italic">No opportunities yet. Head to CRM to register a lead.</div>
        ) : (
          <div className="space-y-2">
            {opps.slice(0, 5).map((o) => (
              <div key={o.id} className="flex items-center justify-between text-xs border-b border-white/5 pb-2 last:border-0">
                <div className="min-w-0">
                  <div className="text-white font-medium truncate">{o.title}</div>
                  <div className="text-white/40 font-mono mt-0.5">{o.customer_name} · {o.category}</div>
                </div>
                <div className="text-right">
                  <div className={`font-mono uppercase tracking-[0.14em] text-[10px] ${OPP_STAGE_TONE[o.stage] || "text-white/60"}`}>{o.stage}</div>
                  {o.estimated_value && <div className="text-white/60 font-mono mt-0.5">{o.currency} {o.estimated_value.toLocaleString()}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {academy && (
        <div className="border border-white/10 rounded-lg p-5 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono mb-3">
            Academy progress — {academy.certifications.length} certification{academy.certifications.length === 1 ? "" : "s"}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {academy.courses.slice(0, 3).map((c) => (
              <div key={c.id} className="border border-white/5 rounded p-3 bg-black/20">
                <div className="text-xs text-white truncate font-medium">{c.title}</div>
                <div className="mt-2 h-1 bg-white/10 rounded overflow-hidden">
                  <div className="h-full bg-[#CCFF00]" style={{width:`${c.progress_pct}%`}} />
                </div>
                <div className="text-[10px] text-white/40 mt-1 font-mono">{c.progress_pct}% · {c.lessons_completed}/{c.lessons_total} lessons</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- CRM ---------------- */
function CRMTab({ partner }) {
  const [leads, setLeads] = useState([]);
  const [opps, setOpps] = useState([]);
  const [newLead, setNewLead] = useState({ customer_name:"", customer_email:"", customer_company:"", category: partner.categories?.[0] || "sales", country: partner.country || "India", estimated_value:"", notes:"" });
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [convertFor, setConvertFor] = useState(null);
  const [stageFor, setStageFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    api.get("/partners/me/leads").then((r) => setLeads(r.data.items)),
    api.get("/partners/me/opportunities").then((r) => setOpps(r.data.items)),
  ]);
  useEffect(() => { load(); }, []);

  const createLead = async () => {
    if (!newLead.customer_name || !newLead.customer_email) {
      toast.error("Customer name + email required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...newLead };
      if (payload.estimated_value) payload.estimated_value = Number(payload.estimated_value);
      else delete payload.estimated_value;
      await api.post("/partners/me/leads", payload);
      toast.success("Lead registered.");
      setShowLeadForm(false);
      setNewLead({ ...newLead, customer_name:"", customer_email:"", customer_company:"", notes:"", estimated_value:"" });
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === "attribution_conflict") toast.error("This customer is already attributed to another partner (90-day window).");
      else toast.error(typeof detail === "string" ? detail : "Couldn't register lead.");
    } finally { setBusy(false); }
  };

  const convert = async (title, estValue) => {
    try {
      await api.post(`/partners/me/leads/${convertFor.id}/convert`, { title, estimated_value: estValue ? Number(estValue) : null });
      toast.success("Lead converted.");
      setConvertFor(null);
      load();
    } catch { toast.error("Couldn't convert."); }
  };

  const advance = async (stage, note, won_amount) => {
    try {
      const r = await api.post(`/partners/me/opportunities/${stageFor.id}/stage`, { stage, note, won_amount: won_amount ? Number(won_amount) : null });
      if (r.data.commissions_generated?.length > 0) toast.success(`Won! ${r.data.commissions_generated.length} commission(s) generated.`);
      else toast.success(`Moved to ${stage}.`);
      setStageFor(null);
      load();
    } catch { toast.error("Couldn't update."); }
  };

  return (
    <div className="space-y-6" data-testid="partner-crm">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-white">Leads & Pipeline</h2>
        <Button onClick={() => setShowLeadForm(!showLeadForm)} data-testid="crm-new-lead-btn"
                className="rounded-full bg-[#CCFF00] text-black font-semibold text-xs">
          <Plus size={13} className="mr-1" /> Register lead
        </Button>
      </div>

      {showLeadForm && (
        <div data-testid="crm-lead-form" className="border border-white/10 rounded-lg p-5 bg-white/[0.02] space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="crm-input-name" placeholder="Customer name *" value={newLead.customer_name} onChange={(e)=>setNewLead({...newLead,customer_name:e.target.value})} className="bg-white/5 border-white/10"/>
            <Input data-testid="crm-input-email" type="email" placeholder="Customer email *" value={newLead.customer_email} onChange={(e)=>setNewLead({...newLead,customer_email:e.target.value})} className="bg-white/5 border-white/10"/>
            <Input placeholder="Company" value={newLead.customer_company} onChange={(e)=>setNewLead({...newLead,customer_company:e.target.value})} className="bg-white/5 border-white/10"/>
            <Input placeholder="Country" value={newLead.country} onChange={(e)=>setNewLead({...newLead,country:e.target.value})} className="bg-white/5 border-white/10"/>
            <select value={newLead.category} onChange={(e)=>setNewLead({...newLead,category:e.target.value})} data-testid="crm-input-category"
                    className="bg-white/5 border border-white/10 text-xs rounded px-2 py-1.5 text-white font-mono">
              {(partner.categories || ["sales"]).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Input type="number" placeholder="Estimated value" value={newLead.estimated_value} onChange={(e)=>setNewLead({...newLead,estimated_value:e.target.value})} className="bg-white/5 border-white/10"/>
          </div>
          <textarea rows={2} placeholder="Notes" value={newLead.notes} onChange={(e)=>setNewLead({...newLead,notes:e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded p-2 text-sm text-white resize-none"/>
          <div className="flex gap-2">
            <Button onClick={createLead} disabled={busy} data-testid="crm-submit-lead" className="rounded-full bg-[#CCFF00] text-black font-semibold text-xs">
              {busy ? "Saving…" : "Save lead"}
            </Button>
            <Button onClick={() => setShowLeadForm(false)} className="rounded-full border border-white/15 bg-transparent text-white text-xs">Cancel</Button>
          </div>
        </div>
      )}

      {/* Leads list */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono mb-2">Leads ({leads.length})</div>
        <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
          {leads.length === 0 && <div data-testid="crm-leads-empty" className="p-6 text-center text-white/40 text-sm">No leads yet.</div>}
          {leads.map((l) => (
            <div key={l.id} data-testid={`crm-lead-${l.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{l.customer_name}</div>
                <div className="text-[11px] text-white/50 font-mono truncate">{l.customer_email} · {l.category}</div>
              </div>
              {l.converted_opportunity_id ? (
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#CCFF00]">converted</span>
              ) : (
                <Button onClick={() => setConvertFor(l)} data-testid={`crm-convert-${l.id}`}
                        className="rounded-full bg-[#D4B878] text-black text-[10px] font-semibold h-auto px-3 py-1">
                  Convert
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Opportunities */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono mb-2">Pipeline ({opps.length})</div>
        <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
          {opps.length === 0 && <div className="p-6 text-center text-white/40 text-sm">Convert a lead to see your pipeline.</div>}
          {opps.map((o) => (
            <div key={o.id} data-testid={`crm-opp-${o.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{o.title}</div>
                <div className="text-[11px] text-white/50 font-mono truncate">
                  {o.customer_name} · {o.currency} {(o.estimated_value || 0).toLocaleString()}
                </div>
              </div>
              <span className={`text-[10px] font-mono uppercase tracking-[0.14em] ${OPP_STAGE_TONE[o.stage] || "text-white/60"}`}>{o.stage}</span>
              {!["won","lost","cancelled"].includes(o.stage) && (
                <Button onClick={() => setStageFor(o)} data-testid={`crm-advance-${o.id}`}
                        className="rounded-full border border-white/15 bg-transparent text-white text-[10px] font-semibold h-auto px-3 py-1">
                  Advance
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>

      {convertFor && <ConvertModal lead={convertFor} onClose={() => setConvertFor(null)} onConfirm={convert} />}
      {stageFor && <StageModal opp={stageFor} onClose={() => setStageFor(null)} onConfirm={advance} />}
    </div>
  );
}

function ConvertModal({ lead, onClose, onConfirm }) {
  const [title, setTitle] = useState(`${lead.customer_name} — ${lead.category}`);
  const [val, setVal] = useState(lead.estimated_value || "");
  return (
    <Modal title="Convert to opportunity" onClose={onClose}>
      <Input value={title} onChange={(e)=>setTitle(e.target.value)} placeholder="Opportunity title" className="bg-white/5 border-white/10"/>
      <Input type="number" value={val} onChange={(e)=>setVal(e.target.value)} placeholder="Estimated value" className="bg-white/5 border-white/10"/>
      <Button onClick={() => onConfirm(title, val)} data-testid="crm-convert-confirm" className="w-full rounded-full bg-[#CCFF00] text-black font-semibold">Create opportunity</Button>
    </Modal>
  );
}

function StageModal({ opp, onClose, onConfirm }) {
  const stages = ["discovery","proposal","negotiation","contracted","fulfilment","won","lost","cancelled"];
  const [stage, setStage] = useState("proposal");
  const [note, setNote] = useState("");
  const [wonAmount, setWonAmount] = useState(opp.estimated_value || "");
  return (
    <Modal title={`Advance "${opp.title}"`} onClose={onClose}>
      <select value={stage} onChange={(e)=>setStage(e.target.value)} data-testid="crm-stage-select"
              className="w-full bg-white/5 border border-white/10 text-xs rounded px-2 py-1.5 text-white font-mono">
        {stages.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      {stage === "won" && (
        <Input type="number" value={wonAmount} onChange={(e)=>setWonAmount(e.target.value)} placeholder="Won amount" className="bg-white/5 border-white/10"/>
      )}
      <textarea rows={2} value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Note (optional)"
                className="w-full bg-white/5 border border-white/10 rounded p-2 text-sm text-white resize-none"/>
      <Button onClick={() => onConfirm(stage, note, wonAmount)} data-testid="crm-stage-confirm"
              className="w-full rounded-full bg-[#CCFF00] text-black font-semibold">Advance</Button>
    </Modal>
  );
}

/* ---------------- MARKETPLACE ---------------- */
function MarketplaceTab({ partner }) {
  const [items, setItems] = useState([]);
  const [applyFor, setApplyFor] = useState(null);
  const [note, setNote] = useState("");

  const load = () => api.get("/partners/me/marketplace").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const apply = async () => {
    try {
      await api.post(`/partners/me/marketplace/${applyFor.id}/apply`, { note });
      toast.success("Application submitted.");
      setApplyFor(null);
      setNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply.");
    }
  };

  return (
    <div data-testid="partner-marketplace" className="space-y-4">
      <h2 className="text-lg font-semibold text-white">Open opportunities</h2>
      {items.length === 0 && (
        <div data-testid="marketplace-empty" className="p-10 border border-dashed border-white/10 rounded text-center text-white/50">
          No open opportunities match your categories ({(partner.categories || []).join(", ")}). Check back soon.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((m) => (
          <div key={m.id} data-testid={`marketplace-card-${m.id}`} className="border border-white/10 rounded-lg p-4 bg-white/[0.02]">
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <div className="text-white font-semibold">{m.title}</div>
                <div className="text-[11px] text-white/50 font-mono mt-1 flex items-center gap-2">
                  <span>{m.category}</span>
                  {m.country && <><span>·</span><MapPin size={9} /><span>{m.country}</span></>}
                </div>
              </div>
              {m.my_application_status && (
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#CCFF00]">
                  {m.my_application_status}
                </span>
              )}
            </div>
            <div className="text-xs text-white/70 leading-relaxed mb-3 line-clamp-3">{m.scope}</div>
            {m.estimated_compensation && (
              <div className="text-[11px] text-[#E8D6A3] font-mono mb-3">💰 {m.estimated_compensation}</div>
            )}
            {!m.my_application_status && (
              <Button onClick={() => setApplyFor(m)} data-testid={`marketplace-apply-${m.id}`}
                      className="rounded-full bg-[#D4B878] text-black text-xs font-semibold w-full">Apply</Button>
            )}
          </div>
        ))}
      </div>
      {applyFor && (
        <Modal title={`Apply to "${applyFor.title}"`} onClose={() => setApplyFor(null)}>
          <textarea rows={4} value={note} onChange={(e)=>setNote(e.target.value)} placeholder="Why you?"
                    className="w-full bg-white/5 border border-white/10 rounded p-2 text-sm text-white resize-none"/>
          <Button onClick={apply} data-testid="marketplace-apply-confirm" className="w-full rounded-full bg-[#CCFF00] text-black font-semibold">Submit application</Button>
        </Modal>
      )}
    </div>
  );
}

/* ---------------- EARNINGS ---------------- */
function EarningsTab({ partner }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const load = () => Promise.all([
    api.get("/partners/me/earnings").then((r) => setData(r.data)),
    api.get("/partners/me/commissions").then((r) => setRows(r.data.items)),
  ]);
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-white/40">Loading…</div>;
  return (
    <div data-testid="partner-earnings-tab" className="space-y-6">
      <PayoutCurrencyCard currentCurrency={data.payout_currency || partner?.payout_currency || "INR"} onUpdate={load}/>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {Object.entries(data.by_status).map(([k, v]) => {
          const meta = COMMISSION_STATE_META[k] || { label: k, tone: "text-white/60" };
          return (
            <div key={k} data-testid={`earnings-bucket-${k}`} className="border border-white/10 rounded p-3 bg-white/[0.02]">
              <div className={`text-[9px] uppercase tracking-[0.18em] font-mono ${meta.tone}`}>{meta.label}</div>
              <div className="text-lg font-semibold text-white font-mono mt-1">{data.currency} {v.amount.toLocaleString()}</div>
              <div className="text-[10px] text-white/40 font-mono">{v.count} row{v.count === 1 ? "" : "s"}</div>
            </div>
          );
        })}
      </div>
      <div className="border border-white/10 rounded-lg bg-white/[0.02] divide-y divide-white/5">
        <div className="p-3 text-[10px] uppercase tracking-[0.22em] text-white/40 font-mono">Ledger ({rows.length})</div>
        {rows.length === 0 && <div className="p-6 text-center text-white/40 text-sm">No commissions yet. Win an opportunity to see rows here.</div>}
        {rows.map((r) => {
          const meta = COMMISSION_STATE_META[r.status] || { label: r.status, tone: "text-white/60" };
          return (
            <div key={r.id} data-testid={`commission-row-${r.id}`} className="flex items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-mono truncate">{r.basis_type}</div>
                <div className="text-[11px] text-white/40 font-mono truncate">on {r.currency} {r.basis_amount.toLocaleString()} @ {(r.rate*100).toFixed(1)}%</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold font-mono text-white">{r.currency} {r.amount.toLocaleString()}</div>
                {r.payout_amount != null && r.payout_currency && r.payout_currency !== r.currency && (
                  <div className="text-[10px] text-[#E8D6A3] font-mono">= {r.payout_currency} {r.payout_amount.toLocaleString()}</div>
                )}
                <div className={`text-[10px] uppercase tracking-[0.14em] font-mono ${meta.tone}`}>{meta.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- ACADEMY ---------------- */
function AcademyTab() {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => api.get("/partners/me/academy").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  if (!data) return <div className="text-white/40">Loading…</div>;
  return (
    <div data-testid="partner-academy-tab" className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {data.courses.map((c) => (
          <div key={c.id} data-testid={`academy-card-${c.id}`} className="border border-white/10 rounded-lg p-4 bg-white/[0.02]">
            <div className="flex items-start justify-between mb-2 gap-2">
              <div>
                <div className="text-white font-semibold">{c.title}</div>
                <div className="text-[11px] text-white/50 mt-1">{c.subtitle}</div>
              </div>
              {c.my_enrollment?.certified_at && <Trophy size={14} className="text-[#CCFF00]"/>}
            </div>
            <div className="mt-3 h-1.5 bg-white/10 rounded overflow-hidden">
              <div className="h-full bg-[#CCFF00]" style={{width:`${c.progress_pct}%`}}/>
            </div>
            <div className="text-[10px] text-white/40 mt-1 font-mono">
              {c.progress_pct}% · {c.lessons_completed}/{c.lessons_total} lessons · {c.quiz.question_count}-q quiz
            </div>
            <Button onClick={() => setActive(c)} data-testid={`academy-open-${c.id}`}
                    className="mt-3 rounded-full bg-[#D4B878] text-black text-xs font-semibold">
              {c.my_enrollment ? "Continue" : "Start course"} <ChevronRight size={11}/>
            </Button>
          </div>
        ))}
      </div>
      {data.certifications.length > 0 && (
        <div className="border border-[#CCFF00]/30 rounded-lg p-4 bg-[#CCFF00]/[0.04]">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#CCFF00] font-mono mb-2">Certifications</div>
          <div className="flex flex-wrap gap-2">
            {data.certifications.map((c) => (
              <span key={c.id} data-testid={`cert-${c.id}`} className="text-xs text-white border border-[#CCFF00]/40 bg-[#CCFF00]/[0.06] rounded-full px-3 py-1 font-mono">
                <Trophy size={10} className="inline mr-1"/> {c.course_title} · {c.score_pct}%
              </span>
            ))}
          </div>
        </div>
      )}
      {active && <CourseRunner course={active} onClose={() => { setActive(null); load(); }} />}
    </div>
  );
}

function CourseRunner({ course, onClose }) {
  const [state, setState] = useState("lessons"); // "lessons" | "quiz" | "result"
  const [enrolled, setEnrolled] = useState(course.my_enrollment);
  const [completed, setCompleted] = useState(new Set(enrolled?.lessons_completed || []));
  const [answers, setAnswers] = useState({});
  const [questions, setQuestions] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!enrolled) {
      api.post(`/partners/me/academy/enroll/${course.id}`).then((r) => setEnrolled(r.data.enrollment));
    }
  }, []);

  const completeLesson = async (lid) => {
    const r = await api.post(`/partners/me/academy/${course.id}/complete-lesson/${lid}`);
    setCompleted(new Set(r.data.lessons_completed));
    toast.success("Lesson complete.");
  };

  const openQuiz = async () => {
    // Fetch quiz with answers would be a security hole — we post answers and get server-side eval.
    // Instead we just show the quiz prompt list from the public course.
    const r = await api.get(`/academy/courses/${course.id}`);
    // Public endpoint doesn't include questions; keep quiz hidden text + options via a special preview route.
    // Simpler: we duplicate questions client-side via the my_academy call that returned them embedded? No, we strip there too.
    // Easiest fix: expose questions (without correct_index) separately. For now, show the lesson summaries and submit button; questions are static per course_id.
    toast("Answer the quiz below.");
    setQuestions(STATIC_QUIZ[course.id] || []);
    setState("quiz");
  };

  const submitQuiz = async () => {
    const r = await api.post(`/partners/me/academy/${course.id}/quiz`, { answers });
    setResult(r.data);
    setState("result");
    if (r.data.passed) toast.success(`Certified · ${r.data.score_pct}%`);
    else toast(`Scored ${r.data.score_pct}% — pass is ${r.data.pass_pct}%.`);
  };

  return (
    <Modal title={course.title} onClose={onClose} wide>
      {state === "lessons" && (
        <>
          {course.lessons.map((l) => (
            <div key={l.id} data-testid={`lesson-${l.id}`} className="border border-white/10 rounded p-3 bg-black/20">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-white font-medium text-sm">{l.title}</div>
                  <div className="text-[10px] text-white/40 font-mono mt-0.5">{l.duration_min} min</div>
                </div>
                {completed.has(l.id) ? (
                  <span className="text-[10px] text-[#CCFF00] font-mono flex items-center gap-1"><Check size={11}/> done</span>
                ) : (
                  <Button onClick={() => completeLesson(l.id)} data-testid={`lesson-complete-${l.id}`}
                          className="rounded-full bg-[#D4B878] text-black text-[10px] font-semibold h-auto px-3 py-1">
                    Mark complete
                  </Button>
                )}
              </div>
              <div className="text-xs text-white/60 mt-2 leading-relaxed">{l.summary}</div>
              {l.key_takeaways && (
                <ul className="mt-2 space-y-1">
                  {l.key_takeaways.map((t,i) => (
                    <li key={i} className="text-[11px] text-white/60 flex items-start gap-1.5">
                      <Check size={10} className="text-[#CCFF00] mt-0.5 shrink-0"/>{t}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          <Button onClick={openQuiz} disabled={completed.size < course.lessons.length}
                  data-testid="academy-take-quiz"
                  className="w-full rounded-full bg-[#CCFF00] text-black font-semibold">
            {completed.size < course.lessons.length ? `Complete ${course.lessons.length - completed.size} more lesson(s) first` : "Take final quiz"}
          </Button>
        </>
      )}
      {state === "quiz" && (
        <>
          {questions.map((q, i) => (
            <div key={q.id} data-testid={`quiz-q-${q.id}`} className="border border-white/10 rounded p-3 bg-black/20">
              <div className="text-sm text-white font-medium mb-2">{i+1}. {q.prompt}</div>
              <div className="space-y-1">
                {q.options.map((opt, idx) => (
                  <label key={idx} className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
                    <input type="radio" name={q.id} checked={answers[q.id]===idx} onChange={()=>setAnswers({...answers,[q.id]:idx})}/>
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
          <Button onClick={submitQuiz} data-testid="academy-submit-quiz" className="w-full rounded-full bg-[#CCFF00] text-black font-semibold">
            Submit answers
          </Button>
        </>
      )}
      {state === "result" && result && (
        <div data-testid="academy-quiz-result" className="text-center space-y-3">
          {result.passed ? (
            <>
              <Trophy size={40} className="mx-auto text-[#CCFF00]"/>
              <div className="text-2xl font-semibold text-white">Certified</div>
              <div className="text-white/60">You scored {result.score_pct}% — pass bar was {result.pass_pct}%.</div>
            </>
          ) : (
            <>
              <AlertTriangle size={40} className="mx-auto text-amber-400"/>
              <div className="text-xl font-semibold text-white">Not quite — {result.score_pct}%</div>
              <div className="text-white/60">You need {result.pass_pct}% to pass. Review the lessons and retake.</div>
              <Button onClick={() => { setState("lessons"); setAnswers({}); }} className="rounded-full bg-[#D4B878] text-black font-semibold">
                Review lessons
              </Button>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}

// Static quiz prompts (mirrored from backend content for display only;
// server evaluates correctness).
const STATIC_QUIZ = {
  "sales-cert": [
    {id:"q1", prompt:"Playback is best described as:", options:["A guaranteed impression","Confirmed delivery of a creative to a screen","A measurement of human attention","The same thing as reach"]},
    {id:"q2", prompt:"The attribution window on customer_email is:", options:["30 days","60 days","90 days","No window — first partner wins forever"]},
    {id:"q3", prompt:"A lead asking for 'guaranteed impressions' should be:", options:["Promised impressions to close","Walked away from","Re-framed around delivery/context/outcomes, or politely declined","Escalated with a special quote"]},
  ],
  "advertising-cert": [
    {id:"q1", prompt:"Agency commissions are calculated on:", options:["Screen uptime hours","Managed campaign revenue","Impression counts","Venue footfall"]},
    {id:"q2", prompt:"Proof-of-play is:", options:["A reach metric","A delivery signal","A footfall estimate","An attention measurement"]},
  ],
  "location-cert": [
    {id:"q1", prompt:"The venue stage before 'Active' is:", options:["Technical Survey","Commissioning","Installation","Site Review"]},
  ],
  "technology-cert": [
    {id:"q1", prompt:"Installation payment is released:", options:["On arrival","After acceptance + evidence","On schedule","Whenever the partner invoices"]},
  ],
  "regional-cert": [
    {id:"q1", prompt:"Regional management fees apply at which lifecycle stage?", options:["Research","Pilot","Active or later","Any stage"]},
  ],
};

/* ---------------- Shared Modal ---------------- */
function Modal({ title, children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className={`bg-[#141110] border border-white/15 rounded-lg w-full p-6 my-10 space-y-3 ${wide ? "max-w-2xl" : "max-w-md"}`}
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div className="text-white font-semibold">{title}</div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={16}/></button>
        </div>
        {children}
      </div>
    </div>
  );
}
